import React, { createContext, useContext, useState, useEffect } from 'react';
import PropTypes from 'prop-types';

const TimeRangeContext = createContext();

export const useTimeRange = () => {
  const context = useContext(TimeRangeContext);
  if (!context) {
    throw new Error('useTimeRange must be used within a TimeRangeProvider');
  }
  return context;
};

export const TimeRangeProvider = ({ children }) => {
  const formatLocalDate = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  // Инициализируем даты из localStorage или используем значения по умолчанию
  const initializeDates = () => {
    const saved = localStorage.getItem('timeRangeSettings');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        return {
          startDate: parsed.startDate || getDefaultStartDate(),
          endDate: parsed.endDate || getDefaultEndDate()
        };
      } catch (e) {
        console.warn('Error parsing saved time range settings:', e);
      }
    }
    return {
      startDate: getDefaultStartDate(),
      endDate: getDefaultEndDate()
    };
  };

  // Получаем даты по умолчанию (последний 1 день) с временем
  const getDefaultStartDate = () => {
    const date = new Date(Date.now() - 1 * 86400000);
    const dateStr = formatLocalDate(date);
    return `${dateStr}T00:00:00`;
  };

  const getDefaultEndDate = () => {
    const date = new Date();
    const dateStr = formatLocalDate(date);
    return `${dateStr}T23:59:59`;
  };

  const [timeRange, setTimeRange] = useState(initializeDates());

  // Сохраняем изменения в localStorage
  useEffect(() => {
    localStorage.setItem('timeRangeSettings', JSON.stringify(timeRange));
  }, [timeRange]);

  const updateStartDate = (date) => {
    setTimeRange(prev => ({ ...prev, startDate: date }));
  };

  const updateEndDate = (date) => {
    setTimeRange(prev => ({ ...prev, endDate: date }));
  };

  const resetToDefaults = () => {
    setTimeRange({
      startDate: getDefaultStartDate(),
      endDate: getDefaultEndDate()
    });
  };

  const value = {
    startDate: timeRange.startDate,
    endDate: timeRange.endDate,
    updateStartDate,
    updateEndDate,
    resetToDefaults
  };

  return (
    <TimeRangeContext.Provider value={value}>
      {children}
    </TimeRangeContext.Provider>
  );
};

TimeRangeProvider.propTypes = {
  children: PropTypes.node.isRequired,
}; 